import React from "react";
import { fmtPct, leadingSide } from "../../../lib/forecast/formatPct.js";
import { STATUS } from "../../../lib/forecast/model.js";
import {
  applyResolutionToDistribution,
  extractDistribution,
  isNamedOutcomeVerdict,
  selectLead,
  winnerOf,
} from "../../../lib/forecast/multiway.js";
import { isVoidedContract, VOIDED_CONTRACT_STATUS } from "../../../lib/forecast/contractStatus.js";
import { alternateDistributionStatus } from "../../../lib/forecast/alternateSpaces.js";
import { isConcluded } from "../../../lib/tracking.js";
import {
  branchTerminalState,
  branchScaffoldDead,
} from "../../../lib/forecast/branchTerminalState.js";
import MultiwayMarker from "../../../components/MultiwayMarker.jsx";
import BranchTerminalPanel, {
  BRANCH_TERMINAL_PANEL_OUTER_W,
} from "../../../components/BranchTerminalPanel.jsx";
import BranchPremiseVerdict from "../../../components/BranchPremiseVerdict.jsx";
import {
  timelineMarkerFootprint,
  timelineMarkerWidth,
} from "../../../lib/timeline/multiwayMarker.js";
import { resolvedReadout, withForecastBars } from "../../../lib/timeline/resolvedOutcome.js";
import { hoveredTrigger, hoverPreviewOf } from "../../../lib/timeline/hoverPreview.js";
import { previewTriggerId } from "../../../lib/telemetry/triggerPreview.js";
import { useTriggerPreviewDwell } from "../../../hooks/useTriggerPreviewDwell.js";
import { measureLabelWidth } from "../../../lib/timeline/measureText.js";
import { APP_FONT_FAMILY } from "../../../lib/appFont.js";
import { buildForkNode } from "../../../lib/timeline/geometry.js";
import { premiseSenseOf, withScenarioOutcomes } from "../../../lib/forecast/branchPremise.js";
import { lookupCalibrationBin } from "../../../lib/calibration.js";
import GlowSpine from "../../../components/timeline/GlowSpine.jsx";
import { filterResolvedEvents } from "../../../lib/forecast/resolvedTimeline.js";
import { buildGlowLines } from "../../../lib/timeline/glowSpineModel.js";
import { C as THEME_C, lightenHex } from "../../../lib/colors.js";
import { surface, ui } from "../../../lib/telemetry/instrument.js";
import LoadingLine from "../../../components/LoadingLine.jsx";
import TerminalSlot from "./TerminalSlot.jsx";
import { awaitsDetail } from "../../../lib/forecast/detailBackfill.js";

const C_TEXT = THEME_C.grayL;

// Dashed line thickness for the timeline spine AND the branch scaffolding
// (vertical fork connector + branch stream). 2/3 of the original 4px — a
// lighter, more delicate dash (CSS scales the dash LENGTH with the border
// thickness, so the dashes also shorten proportionally). Shared by every dashed
// segment so the spine↔connector↔stream joints all stay visually consistent.
const SPINE_DASH_PX = 2.67;
// The settled main spine: a thin solid line (dashes stay for in-flux lines).
const MAIN_SPINE_PX = 1.5;
// A `borderTop` renders its thickness DOWNWARD from the element's top edge, so a dashed
// spine whose top edge sits at the logical spine Y is visually centered half a thickness
// too low — which makes the on-spine event circles/dots (centered exactly on spineY) and
// the multiway bar chart (centered at spineY) read as sitting slightly high on the line.
// Lift every horizontal dashed segment by half its thickness so the LINE's visual center
// lands on the logical Y, aligning with the circles/dots and the bar-chart center.
const SPINE_DASH_HALF = SPINE_DASH_PX / 2;

// The "Generating Forecast" status sits INLINE on the spine, right of the forecast line.
// The distribution marker occupies that same space — it hangs off the line, centred on the
// spine — so during a REFORECAST (the previous forecast's bars still on screen) the status
// clears it HORIZONTALLY rather than by lifting off the line: `timelineMarkerWidth` reports
// what the bars will take, and the status starts past that. With no marker, it starts just
// past the line. Either way it stays on the spine at every zoom level, since both offsets
// are measured from the line's own x.
const GENERATING_LABEL_FONT = 12;
const GENERATING_LABEL_GAP = 12;
// MEASURED IN THE FACE THAT ACTUALLY RENDERS. The label's own inline style says IBM Plex
// Mono, but GlobalStyles forces APP_FONT_FAMILY on every element with `!important`, so that
// is a dead declaration — and measuring the mono face it names over-states the width by about
// a third, which is precisely the bug: the old `length * (12 * 0.58)` estimate was built on
// those mono metrics. The tracking, by contrast, IS the inline 1px (the global rule's 1px
// default carries no `!important`, so an element's own value wins).
const GENERATING_LABEL_FAMILY = APP_FONT_FAMILY;
const GENERATING_LABEL_TRACKING = 0;
// The in-progress status is the shared spinner + "Forecasting…" (LoadingLine),
// not a pulsing caps label. Its box is the spinner (16px) + gap (6px) + the measured text.
const FORECASTING_LABEL = "Forecasting…";
const GENERATING_SPINNER_W = 16 + 6;
const GENERATING_LINE_H = 16;
const GENERATING_LINE_STYLE = { fontSize: GENERATING_LABEL_FONT, color: C_TEXT, gap: 6 };
// Breathing room between the label's type and the gap it sits in, per side. Small because
// the width it pads is now MEASURED (see measureLabelWidth) rather than guessed — the old
// per-character estimate had to over-reserve by ~24px a side to stay off the glyphs under any
// face, and all of that showed up as empty line. With the real width in hand this is the
// whole gap: the dashes fade back in over the 8px past it.
const GENERATING_LABEL_PAD = 3;
// The measured label box: real text width plus a pad on each side. Shared by the awaiting
// spine (which cuts its dash gap to exactly this) and the reforecast overlay.
const generatingLabelBox = (text) =>
  measureLabelWidth(text, {
    fontSize: GENERATING_LABEL_FONT,
    fontFamily: GENERATING_LABEL_FAMILY,
    letterSpacing: GENERATING_LABEL_TRACKING,
  }) +
  GENERATING_SPINNER_W +
  GENERATING_LABEL_PAD * 2;

// ───────────────────────────────────────────────────────────────────────────
// <ForecastLane> — selection-agnostic render of ONE forecast lane.
//
// Renders, for a single RAW store run (`root`):
//   • the root spine + stream (awaiting / settled treatment + past-mask),
//   • the lane's events (via the shared `renderRow` primitive),
//   • the resolution marker (probability pie + label + verdict overlay), and
//   • the branch subtree (fork connectors, "Branch N" labels, branch events,
//     branch resolution circles).
//
// The component reads NOTHING from the global selection state (`active`,
// `forecasts`, `comparison`, `compareOn`); every value it needs is supplied
// through `config` (visual only), `layout` (geometry), and `ctx` (the shared
// render-primitive bundle the lane closed over when it lived inline in App.jsx).
//
// This is a behavior-preserving extraction: the JSX below is ported VERBATIM
// from the App.jsx primary-lane render (the spine IIFE ~7582, renderRow call
// ~7674, the resolution marker ~8058, and the renderLaneBranches body ~8210),
// with the only edits being (a) closed-over symbols re-sourced from `ctx`, and
// (b) the branch subtree reading the RAW run shape (runId / parentRunId /
// contract.probability / status) instead of the legacy-converted branch shape.
//
// Props:
//   root    — a RAW store run: { runId, forecastId, contract, events, backend,
//             status, ... }. The lane's spine/events/marker.
//   branches— this lane's flat descendant list of RAW branch runs (DFS pre-order,
//             matching what renderLaneBranches iterated). The caller resolves it
//             via selectBranches/selectRunTree.
//   scenarios— every branch run in the tree, cleared ones included (defaults to `branches`);
//             read only for the triggers' "?" marks (withScenarioOutcomes).
//   config  — visual-only bag: { laneIndex, isPrimary, color, palette, showEngine }.
//   layout  — geometry: { spineY, branchYs } (one lane of stackedLaneLayout).
//   ctx     — shared render-primitive bundle (see App.jsx assembly), grouped:
//             geom / scale / anim / colors / render / hover / branch / calib /
//             consts / components / view.
// ───────────────────────────────────────────────────────────────────────────
export default function ForecastLane({ root, branches, scenarios, config, layout, ctx }) {
  const { color } = config;
  // compareOn is a per-lane VISUAL flag (not a global selection read): in a
  // compare render every lane sets it true so the branch scaffolding tints with
  // the lane color and the resolution marker shows the engine cue; in a
  // single-forecast render it is false (gray scaffolding, no engine cue),
  // matching the old `!compareOn` dispatch. Driven by config — never the store.
  const compareOn = !!config.compareOn;
  const spineY = layout.spineY;
  const branchYs = React.useMemo(() => layout.branchYs || [], [layout.branchYs]);

  // ── ctx unpack ────────────────────────────────────────────────────────────
  const { dayToScreen, vw, effectiveToday, todayX, rightInset = 0 } = ctx.geom;
  const { animPhase, branchAnim } = ctx.anim;
  const { C, redBlueTeam, isLight } = ctx.colors;
  const { renderRow } = ctx.render;
  const { hov, hovPos, setHov, setHovPos, hoveredBranch, setHoveredBranch, setHoveredBin, uiZoom } =
    ctx.hover;
  // Spine-hover is per-lane LOCAL state — NOT threaded through the shared
  // `ctx.hover` bundle. Hovering one lane's spine must brighten only THAT lane's
  // dashes; the pre-extraction compare block kept a separate `hoveredCompareLane`
  // index for exactly this so the primary never cross-highlighted. A self-contained
  // lane just owns the boolean. (`hoveredBranch` stays global: the TSD chart
  // highlights branch paths by run id across lanes.)
  const [hoveredMainSpine, setHoveredMainSpine] = React.useState(false);
  const { removeBranch, toggleBranchCollapsed, openReport, premiseViews, clearBranchPremise } =
    ctx.branch;
  const { forecastTracking, calibration, CALIBRATION_MODEL_ID } = ctx.calib;
  const { RES_SZ, STREAM_HALF, parseResolveDateLocal, resolutionMomentLocal, resolutionClampIso } =
    ctx.consts;
  const { ResolutionIcon, Tooltip } = ctx.components;
  const {
    forecasterDebugLabel,
    rootEngine,
    debugResolutionOverride,
    scrubbedToForecast,
    primaryVerdictStatus,
    // chatboard-all-multiway (from /api/me): renders a BINARY lane through the
    // multi-value surface — neutral triggers + a two-bar distribution, no pie.
    allMultiway,
  } = ctx.misc;

  const active = root;
  const resDate = active?.contract?.resolution_date;

  // Early-resolve gate (primary lane only). App.jsx folded the terminal-verdict +
  // earlier-than-horizon checks into ctx.misc.earlyResolved, so here we only gate on
  // the lane. Hoisted above shownMain because shownMain drops triggers past the
  // actual resolve date when this is true (the system shouldn't forecast past the
  // point the question settled). markerX (below) needs resScreenX so it stays there.
  const laneEarlyResolved =
    config.laneIndex === 0 && !!ctx.misc.earlyResolved && ctx.misc.resolvedScreenX != null;
  // This lane shows a TERMINAL verdict. Hoisted out of shownMain because the spine
  // render below needs it as well: a resolved lane must still draw its timeline even
  // when the drop filter leaves it with zero triggers. Null while scrubbed to a
  // forecast vintage (see App.jsx `scrubbedToForecast`), so a rewound pre-resolution
  // view is unaffected.
  // `primaryVerdictTerminal` covers BOTH shapes of settled question: the binary
  // `resolved_*` tokens and a multi-value resolution, which names an outcome and carries
  // no status token (App.jsx `_resHasTerminal`). Testing the three strings here instead
  // skipped the resolved path for every categorical question.
  const laneResolvedTerminal = config.laneIndex === 0 && !!ctx.misc.primaryVerdictTerminal;

  // ── per-lane spine derivations (review note b: ONE gating source) ──────────
  // Every lane derives its own spine events + resolution screen-X from its OWN
  // `root` (a raw run) — NOT from the global active/primary `view` bundle. This
  // is what lets the SAME component render the primary lane and every secondary
  // compare lane identically: the marker, spine extent, and event clamping all
  // key off `root`, so there is no primary-vs-secondary control flow.
  //   • shownMain — root.events clamped to the lane's own resolution_date and
  //     sorted by date (mirrors App.jsx `mainEvents`).
  //   • resScreenX — the lane's resolution_date in screen-space, via the same
  //     resolutionMomentLocal end-of-day shift App.jsx applies (no Dec-31 special
  //     case anymore) so the purple line lays over the tick.
  const shownMain = (() => {
    // resolutionMomentLocal: the resolution LINE moment (end-of-day for a date-only
    // resolution, start-of-day for a "before <date>" question, exact time if given) —
    // the SAME helper resScreenX uses, so the clamp boundary and the line stay in
    // lockstep (no UTC-vs-local drift). UTC-tz CI is identical.
    const rdRaw = resDate ? resolutionMomentLocal(resDate, active?.question) : null;
    // Guard an unparseable resolution_date: null out rd (clamp becomes a no-op).
    const rd = rdRaw && !isNaN(rdRaw.getTime()) ? rdRaw : null;
    // DROP boundary: once the forecast is RESOLVED (primary lane with a terminal
    // verdict — read from primaryVerdictStatus, which is null while scrubbed to a
    // FORECAST vintage, so a scrubbed pre-resolution view still shows its triggers),
    // the question is SETTLED. A settled question has no PENDING triggers — only events
    // that already OCCURRED remain, so the filter keeps just `isPast` events dated at or
    // before this boundary and drops everything else (all not-yet-occurred triggers, and
    // anything dated after the resolution). Earlier attempts merely repositioned pending
    // triggers onto the marker; they still rendered as pending pies to its right — the
    // rule is now "resolved ⇒ no pending triggers." dropBoundary also still feeds the
    // clamp below (caps a surviving past event's date_end/mean_date at the marker).
    //
    // The boundary MIRRORS the marker geometry / resolvedCapMs rule: use the actual
    // resolve date ONLY when the forecast resolved EARLY (before its horizon — where
    // the marker sits at resolvedScreenX); otherwise (resolved AT/AFTER the horizon,
    // or an unparseable resolved_at) use the horizon rd (where the marker sits). Using
    // a late resolved_at here would keep past-horizon triggers that the clamp then
    // misplaces onto the horizon line, disagreeing with the marker.
    const _resolvedPrimary = laneResolvedTerminal;
    const _earlyResolvedAt =
      laneEarlyResolved && ctx.misc.resolvedAtIso
        ? parseResolveDateLocal(ctx.misc.resolvedAtIso)
        : null;
    const dropBoundary = _resolvedPrimary
      ? _earlyResolvedAt && !isNaN(_earlyResolvedAt.getTime())
        ? _earlyResolvedAt
        : rd
      : null;
    // CLAMP boundary: pull any trigger position past the boundary back onto it. For a
    // RESOLVED forecast the boundary is the MARKER moment (dropBoundary — the resolve
    // date when early-resolved), NOT the horizon rd. The drop above only filters on a
    // trigger's `date`, so a KEPT trigger (early `date`) can still carry a mean_date or
    // date_end between the resolve date and a distant horizon; clamping to rd left its
    // circle anchored to the RIGHT of the occurred pie. Clamping to dropBoundary keeps
    // every surviving trigger at/left of the marker. Unresolved → rd (unchanged).
    // resolutionClampIso formats the target from LOCAL components so the clamped event's
    // new Date() lands exactly on the line. (Shared with App.)
    const boundary = dropBoundary || rd;
    const bIso = resolutionClampIso(boundary);
    const boundaryMs = boundary ? boundary.getTime() : null;
    // Event date → ms via the SAME local convention; an invalid/absent date yields
    // NaN so it's dropped by the <= filter (matching the prior `new Date(...)` where
    // NaN comparisons are false).
    const evMs = (v) => {
      const d = v ? parseResolveDateLocal(v) : null;
      return d && !isNaN(d.getTime()) ? d.getTime() : NaN;
    };
    return (
      // Resolved: the question is SETTLED, so only triggers that had already OCCURRED by
      // the resolution moment survive — decided against the boundary, NOT the stored
      // `isPast` (see filterResolvedEvents). No-op while unresolved.
      filterResolvedEvents(active?.events, dropBoundary ? dropBoundary.getTime() : null, evMs)
        .map((e) => {
          if (!boundary) return e;
          // Clamp past-boundary positions back onto the line. mean_date must be
          // clamped too — the circle is anchored at mean_date first (eventAnchorMs),
          // so an unclamped mean_date renders the trigger past the resolution line.
          if (evMs(e.date) > boundaryMs)
            return { ...e, date: bIso, date_end: null, mean_date: null };
          const out = { ...e };
          if (e.date_end && evMs(e.date_end) > boundaryMs) out.date_end = bIso;
          if (e.mean_date && evMs(e.mean_date) > boundaryMs) out.mean_date = bIso;
          return out;
        })
        .slice()
        .sort((a, b) => evMs(a.date) - evMs(b.date))
    );
  })();
  const resScreenX = resDate
    ? (() => {
        // resolutionMomentLocal (see shownMain): end-of-day for a date-only
        // resolution so the lane's forecast line lands on the correct tick.
        const d = resolutionMomentLocal(resDate, active?.question);
        // Invalid resolution_date → null (not dayToScreen(NaN)); consumers
        // fall back on null (rxClamped, widths), but NaN would poison them.
        if (!d || isNaN(d.getTime())) return null;
        return dayToScreen(d);
      })()
    : null;
  // markerX is where the verdict pie/glyph/label and the solid end of the future
  // spine sit — the actual resolve date when early-resolved, else the horizon. The
  // purple forecast line stays at resScreenX. When NOT early-resolved, markerX ===
  // resScreenX so every downstream expression reduces to its original form (normal
  // timeline is byte-identical). laneEarlyResolved is hoisted above shownMain.
  const markerX = laneEarlyResolved ? ctx.misc.resolvedScreenX : resScreenX;
  // Per-lane awaiting predicate. Lane 0 (single + compare primary) follows the
  // global forecast phase; secondary lanes carry no global phase, so the caller
  // passes a data-driven per-lane predicate via config.awaiting. Either way the
  // lane reads ONE flag and never branches on selection.
  const forecastingPhase = config.awaiting ? "awaiting" : null;
  // During a REFORECAST (awaiting with old events still on screen) the spine +
  // events stay FULLY VISIBLE — the "Generating Forecast" pulse below is the only
  // in-progress cue. (They used to fade to 0.28, which dimmed the forecast;
  // removed per product request — reforecast no longer dims the background.)
  const regenOpacity = 1;

  // ── raw-branch field adapters (renderLaneBranches expected the legacy branch
  // shape; raw runs rename a few fields — adapt at the read site so the rest of
  // the ported body is byte-identical) ──────────────────────────────────────
  const laneBranches = React.useMemo(() => branches || [], [branches]);
  const branchId = (b) => b.runId; // raw run identity (was the legacy branch.id)
  const branchParentId = (b) => (b.parentRunId === root.runId ? null : b.parentRunId); // top-level fork → null
  const branchProbability = (b) => b.contract?.probability ?? null;
  const branchLoading = (b) => b.status === STATUS.PREPROCESSING || b.status === STATUS.RUNNING;
  // The scenarios forked off a row's triggers, which decide its "?" marks (withScenarioOutcomes).
  const childrenOf = (runId) =>
    runId ? (scenarios || laneBranches).filter((b) => b.parentRunId === runId) : [];
  // Main-spine "a branch is hovered" brightening must react only to THIS lane's
  // branches. `hoveredBranch` is a global run id (shared so the TSD chart can
  // highlight across lanes), so gate the spine reaction on own-lane membership —
  // otherwise hovering a branch in another compare lane would brighten this spine.
  const ownBranchHovered =
    hoveredBranch != null && laneBranches.some((b) => branchId(b) === hoveredBranch);

  // ── Experimental glow spine (ctx.glow.enabled) ─────────────────────────────
  // When on, the dashed spine + branch connectors + branch label-gaps below are
  // gated OFF and replaced by a single SVG <GlowSpine> per lane. The interactive
  // event pies (renderRow), resolution markers, and tooltips are untouched — the
  // glow layer is display-only (pointerEvents:none) and sits BEHIND them, so the
  // occurs/not-occurs tooltip → handleBranch fork flow keeps working exactly as
  // before. Off by default → dashed spine renders byte-identically.
  const glow = ctx.glow || {};
  const glowEnabled = !!glow.enabled;
  // Pure derivation (no timestamps — GlowSpine's rAF loop owns entrance births).
  const glowLines = React.useMemo(() => {
    if (!glowEnabled) return [];
    return buildGlowLines({
      root,
      rootId: root?.runId,
      branches: laneBranches,
      spineY,
      branchYs,
      dayToScreen,
      mainGenerating: forecastingPhase === "awaiting",
      // Inline the loading predicate (not the outer `branchLoading`, which is a
      // fresh function each render): it closes over only the STATUS module
      // constant, so the memo's dep array stays complete without depending on an
      // unstable function identity that would defeat the memo.
      branchGenerating: (b) => b.status === STATUS.PREPROCESSING || b.status === STATUS.RUNNING,
    });
  }, [glowEnabled, root, laneBranches, spineY, branchYs, dayToScreen, forecastingPhase]);
  // SVG must be tall enough to contain this lane's lowest branch (its internal
  // coords are the absolute timeline Ys). Width = viewport; overflow is visible.
  // Filter branchYs to finite values first (same guard buildGlowLines applies):
  // a mid-render non-finite entry would make Math.max return NaN → a NaN SVG height.
  const finiteBranchYs = branchYs.filter((y) => Number.isFinite(y));
  const glowHeight = glowEnabled
    ? Math.max(spineY, ...(finiteBranchYs.length ? finiteBranchYs : [spineY])) + 160
    : 0;
  const glowHoveredId = hoveredBranch ?? (hoveredMainSpine ? "main" : null);

  // ── ALL MULTIWAY (chatboard-all-multiway) ──────────────────────────────────
  // One UX regardless of question type: with the flag on a BINARY lane renders the
  // same way a multi-value one does — neutral triggers on the spine and a two-bar
  // distribution in place of the probability pie.

  // This lane's verdict state, computed ONCE and consumed by both markers. It used to
  // live inside the pie block; the distribution marker needs the same answer, and two
  // copies of this precedence chain would be two chances to disagree about what
  // "resolved" means on the same lane.
  const laneVerdict = (() => {
    // None at a scrubbed forecast vintage, not just no status: a multi-value verdict names
    // its slot without one, and the distribution marker collapses onto that slot by itself.
    const verdict = forecastTracking && !scrubbedToForecast ? active?.resolution : null;
    // Scrub-vs-verdict precedence, ENTRY-KIND-DRIVEN. The PRIMARY lane (laneIndex 0)
    // uses App's already-resolved `primaryVerdictStatus`, which reflects the SELECTED
    // resolution attempt (scrubbing to an older attempt shows ITS status), the
    // scrub-to-forecast suppression, and the debug override — one source of truth
    // shared with the marker geometry + modal. Compare lanes (scrub disabled) derive
    // their own from per-lane root.resolution, still suppressed while a forecast
    // vintage is scrubbed.
    const verdictStatus =
      config.laneIndex === 0
        ? (primaryVerdictStatus ?? null)
        : scrubbedToForecast
          ? null
          : debugResolutionOverride || verdict?.status;
    const isResolvedYes = verdictStatus === "resolved_yes";
    const isResolvedNo = verdictStatus === "resolved_no";
    const isResolvedTie = verdictStatus === "resolved_tie";
    const isUnresolved = verdictStatus === "unresolved";
    // A VOID is terminal without a verdict, so it cannot be derived from `verdictStatus`
    // the way the three above are — read through the verdict alone it is indistinguishable
    // from "the resolver never ran". Taken from the contract status, which is what the
    // server writes for it (see lib/forecast/contractStatus.js). Not gated on
    // `resolutionDateInPast` below: a void is terminal when stamped, not when a date passes.
    const isVoided = !debugResolutionOverride && !scrubbedToForecast && isVoidedContract(active);
    const hasTerminalVerdict = isResolvedYes || isResolvedNo || isResolvedTie;
    // The same question asked of the STORED row, via the app's own answer to "is this
    // concluded". TWO differences from `hasTerminalVerdict` above, and both are required
    // for the client to agree with the server about whether a clear will be accepted:
    //
    //   * It ignores the debug Force-Resolution override. That override is right for the
    //     visual — previewing a state the data does not hold is its whole point — but the
    //     server reads the root's persisted `server_ctx.resolution`, so a forced verdict
    //     would light the button and then take a 409.
    //   * It is WIDER than the binary triad, which drives the verdict visual and stops
    //     there. A root can also be terminal by `resolved_multi`, by a void (terminal with
    //     no verdict at all), or by a bus-delivered multiway slot carrying no status token
    //     — and clearBranchPremise.js accepts all three, so refusing them here would leave
    //     the button dead on a root the server would happily clear under.
    //
    // `isConcluded` is that predicate, shared rather than restated so the two cannot drift.
    const hasPersistedTerminalVerdict = isConcluded(active);
    // resolutionMomentLocal (end-of-day for a date-only resolution) so a forecast
    // resolving *today* is NOT treated as already-past: its moment is end-of-today,
    // still ahead of "now".
    const _resParsed = active.contract?.resolution_date
      ? resolutionMomentLocal(active.contract.resolution_date, active?.question)
      : null;
    const resolutionDateMs =
      _resParsed && !isNaN(_resParsed.getTime()) ? _resParsed.getTime() : null; // guard null/invalid → not-in-past
    const resolutionDateInPast =
      resolutionDateMs != null && resolutionDateMs < effectiveToday.getTime();
    // A MULTI-VALUE verdict settles by naming an outcome and carries no yes/no/tie token,
    // so `hasTerminalVerdict` cannot see it. Without this, a resolved categorical question
    // past its date fell through to `effectiveUnresolved` below and handed the distribution
    // marker a synthetic "unresolved" — which `applyResolutionToDistribution` treats as the
    // resolver's own word and lets win over the named slot. The primary lane takes App's
    // `primaryVerdictTerminal` (already resolved against the selected attempt + scrub, with
    // the same `isNamedOutcomeVerdict` precedence); compare lanes ask their own root. The
    // debug override stays authoritative.
    const namesOutcomeVerdict =
      !hasTerminalVerdict &&
      !debugResolutionOverride &&
      !scrubbedToForecast &&
      (config.laneIndex === 0 ? laneResolvedTerminal : isNamedOutcomeVerdict(verdict));
    // A forecast past its resolution moment with NO terminal verdict is UNRESOLVED —
    // whether the resolver ran and returned "unresolved" OR never ran at all (in which
    // case active.resolution is null, so isUnresolved is false). Once the forecast line
    // is in the past it must NEVER show a live %. Tracking-gated (the verdict lifecycle
    // only exists under forecast tracking) and scrub-suppressed (a scrubbed forecast
    // vintage predates resolution).
    const effectiveUnresolved =
      !hasTerminalVerdict &&
      !namesOutcomeVerdict &&
      !scrubbedToForecast &&
      (debugResolutionOverride === "unresolved" ||
        ((isUnresolved || forecastTracking) && resolutionDateInPast));
    return {
      verdict,
      verdictStatus,
      isResolvedYes,
      isResolvedNo,
      isResolvedTie,
      hasTerminalVerdict,
      hasPersistedTerminalVerdict,
      effectiveUnresolved,
      isVoided,
      showVerdictVisual: hasTerminalVerdict || effectiveUnresolved || isVoided,
      // The status the DISTRIBUTION marker reacts to. Not simply `verdictStatus`: a
      // forecast is effectively unresolved once past its resolution moment even when
      // the resolver never ran at all, and in that case verdictStatus is null.
      // The token the marker badges itself with, so a void needs its own rather than
      // borrowing "unresolved" — the two ask for identical geometry but different words.
      distStatus: isVoided
        ? VOIDED_CONTRACT_STATUS
        : hasTerminalVerdict
          ? verdictStatus
          : effectiveUnresolved
            ? "unresolved"
            : null,
    };
  })();

  // The distribution this lane draws INSTEAD of the pie, or null for the ordinary
  // binary pie path. A multi-value contract (no single P(YES)) always takes it — the
  // pre-flag behaviour, gated on `probability == null` exactly as before. A binary one
  // takes it only under All Multiway, so with the flag off nothing about this lane
  // changes.
  const laneDist = extractDistribution(active?.contract, {
    allMultiway,
    probability: active?.contract?.probability,
  });
  // Collapsed to 100/0 on the outcome that occurred once a verdict names one (binary
  // always does; multi-value not until a resolution carries a bucket). Suppressed while
  // the verdict visual is suppressed, so a scrubbed forecast vintage keeps its bars.
  // A lane shown in its ALTERNATE space (useAlternateSpaceDefault) carries the verdict
  // re-keyed to that space; the contract's own names an outcome in the question's space.
  // A scrubbed forecast vintage predates the verdict, so it gets none (laneVerdict.verdict
  // is null there).
  const altLaneResolution =
    laneVerdict.verdict && active && "distributionResolution" in active
      ? active.distributionResolution
      : undefined;
  // The collapse names the outcome that happened; the bars keep Sayer's own
  // final forecast so the block reads as forecast vs outcome (lib/timeline/resolvedOutcome.js).
  const laneDistSettled = laneDist
    ? applyResolutionToDistribution(
        laneDist,
        altLaneResolution === undefined
          ? { status: laneVerdict.distStatus, resolution: laneVerdict.verdict }
          : {
              status: alternateDistributionStatus(
                laneVerdict.distStatus,
                altLaneResolution,
                laneVerdict.verdictStatus,
              ),
              resolution: altLaneResolution,
            },
      )
    : null;
  const laneDistResolved = withForecastBars(laneDist, laneDistSettled);
  // The winning row, for the hover readout under the question.
  const winnerReadout = resolvedReadout(laneDistResolved);
  // While one of this lane's triggers is hovered, the distribution block
  // previews the forecast if it happens (lib/timeline/hoverPreview.js).
  const hoveredEv = laneDistResolved
    ? hoveredTrigger(
        hov,
        config.laneIndex === 0 ? "main" : "compare-" + config.laneIndex,
        shownMain,
      )
    : null;
  const hoverPreview = laneDistResolved
    ? hoverPreviewOf(hoveredEv, laneDistResolved, {
        resolutions: forecastTracking ? root.triggerResolutions : null,
      })
    : null;
  // How long the preview is looked at (`ui.trigger_preview`, ids only): only while the
  // distribution marker that draws it is on screen (the gate of DISTRIBUTION MARKER below).
  const previewDrawn =
    !!hoverPreview &&
    resScreenX !== null &&
    markerX != null &&
    markerX > -40 &&
    markerX < vw + 40 &&
    !(allMultiway && animPhase && animPhase.revealCount < animPhase.totalEvents);
  useTriggerPreviewDwell({
    forecastId: root.runId,
    triggerId: previewDrawn ? previewTriggerId(hoveredEv, shownMain) : null,
    from: "timeline",
  });
  // Below every hook: the detail landing, or its read giving up, flips this mid-life.
  if (awaitsDetail(root, ctx.misc.detailFailed)) {
    return (
      <div {...surface("timeline.lane", root.runId)}>
        <div
          data-testid="forecast-detail-loading"
          style={{
            position: "absolute",
            left: Math.max(16, Math.min(vw - 200, todayX + GENERATING_LABEL_GAP)),
            top: spineY - GENERATING_LINE_H / 2,
            pointerEvents: "none",
          }}
        >
          <LoadingLine style={GENERATING_LINE_STYLE}>Loading forecast…</LoadingLine>
        </div>
      </div>
    );
  }

  return (
    // The lane's attention surface. A plain block with only absolutely positioned children: it
    // has no height and changes no layout, so the tracker reads it as boxless (visible while the
    // timeline is).
    <div {...surface("timeline.lane", root.runId)}>
      {/* Invisible marker so visual tests can wait for a secondary compare lane to render (display:none → no layout/pixels). */}
      {compareOn && config.laneIndex >= 1 && (
        <div data-compare-lane={config.laneIndex} style={{ display: "none" }} />
      )}
      {/* === GLOW SPINE (experimental, debug-only) === display-only overlay that
          replaces the dashed spine/connectors/label-gaps below; sits behind the
          interactive event layer (pointerEvents:none) so forking is untouched. */}
      {glowEnabled && glowLines.length > 0 && (
        <div
          data-glow-spine={config.laneIndex}
          data-telemetry-ticking=""
          style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 2 }}
        >
          <GlowSpine
            width={vw}
            height={glowHeight}
            spineY={spineY}
            lines={glowLines}
            events={[]}
            tokens={C}
            controls={glow.controls}
            hoveredId={glowHoveredId}
            onHover={(id) => {
              // The line's 20px hit-path (pointerEvents:stroke) captures hover even
              // though the wrapper is pointerEvents:none; map it to the same
              // hoveredBranch/hoveredMainSpine the dashed spine drove, so lineage
              // highlight (here + the TSD chart) works again with the glow on.
              if (id == null) {
                setHoveredMainSpine(false);
                setHoveredBranch(null);
              } else if (id === "main") {
                setHoveredMainSpine(true);
                setHoveredBranch(null);
              } else {
                setHoveredBranch(id);
                setHoveredMainSpine(false);
              }
            }}
          />
        </div>
      )}
      {/* === SPINE / STREAM === */}
      {/* The dotted line is the question's timeline, not a function of how many triggers
        it has: a forecast with a resolution date (`resScreenX`) draws it with none, and
        `laneResolvedTerminal` keeps it for a RESOLVED lane the drop filter emptied.
        Gating on the trigger list read as "this forecast never existed". */}
      {/* `config.hideAwaiting`: the RunStatus card covers a first run, so the
          spine and its "Generating Forecast" label stay hidden until the answer arrives. */}
      {!glowEnabled &&
        active &&
        !config.hideAwaiting &&
        (shownMain.length > 0 ||
          forecastingPhase === "awaiting" ||
          laneResolvedTerminal ||
          resScreenX !== null) &&
        (() => {
          const pastEvts = shownMain.filter((e) => e.isPast);
          const isAwaiting = forecastingPhase === "awaiting" && shownMain.length === 0;
          const firstPastX = pastEvts.length ? dayToScreen(pastEvts[0].date) : -999;
          const txClamped = Math.max(-20, Math.min(vw + 20, todayX));
          const rxClamped =
            resScreenX !== null ? Math.max(-20, Math.min(vw + 20, resScreenX)) : txClamped;
          const rawPastLeft = Math.min(firstPastX, txClamped);
          // Bounded to ONE SCREEN of lead-in. `firstPastX` is the oldest past event's raw
          // screen x and is deliberately NOT clamped (unlike txClamped/rxClamped), and the
          // `* 2` below triples the distance to give `pastMask` a run-up off-screen. With a
          // recent first event that is a few hundred px and works as designed; with an old
          // one (a trigger dated years back) at a high zoom it ran to ~11.5 MILLION px.
          // `pastMask` is `linear-gradient(to right, transparent, black)` — relative to the
          // element's OWN width — so across the ~1500px actually on screen the gradient had
          // barely left "transparent": the spine was drawn and then faded to invisibility.
          // Zooming out shrank the span until the gradient reached visible opacity, which is
          // exactly why it came back. The clamp only bites in that runaway case: a normal
          // lead-in is far smaller than vw and passes through untouched.
          const pastLeft = pastEvts.length
            ? Math.max(-vw, rawPastLeft - (txClamped - rawPastLeft) * 2)
            : 0;
          const pastW = Math.max(0, txClamped - pastLeft);
          const futW = Math.max(0, rxClamped - txClamped);
          // A RESOLVED lane the drop filter left with NO survivors: `futW` is 0 whenever the
          // resolution horizon sits at or before today, so the past segment must span all the
          // way to it or the lane renders only its invisible hover target.
          const terminalEmpty = !isAwaiting && laneResolvedTerminal && pastEvts.length === 0;
          // Span the settled question: lane edge (pastLeft is 0 with no survivors) to the
          // clamped resolution HORIZON (rxClamped, from resScreenX — not markerX, which an
          // early resolve pulls earlier). rxClamped falls back to today when resScreenX is null.
          const spineW = terminalEmpty ? Math.max(0, rxClamped - pastLeft) : pastW;
          const pastMask = !pastEvts.length
            ? `linear-gradient(to right, transparent 0%, black 25%, black 100%)`
            : `linear-gradient(to right, transparent, black)`;
          // Past the moment a settled question resolved the spine recedes with
          // the canvas (App dims the canvas there; the lane sits above that overlay).
          const resolvedCut =
            laneResolvedTerminal && markerX != null ? Math.max(0, markerX - pastLeft) : null;
          const pastSpineMask =
            resolvedCut != null && resolvedCut < spineW
              ? {
                  maskImage: `${pastMask}, linear-gradient(to right, black ${resolvedCut}px, rgba(0,0,0,0.3) ${resolvedCut}px)`,
                  WebkitMaskImage: `${pastMask}, linear-gradient(to right, black ${resolvedCut}px, rgba(0,0,0,0.3) ${resolvedCut}px)`,
                  maskComposite: "intersect",
                  WebkitMaskComposite: "source-in",
                }
              : { maskImage: pastMask, WebkitMaskImage: pastMask };
          const streamAnim = isAwaiting
            ? "branchHGrow 0.8s ease-out 1.4s both"
            : animPhase && !animPhase.skipPastSweep
              ? "branchHGrow 0.8s ease-out"
              : "none";
          const resultLineAnim =
            animPhase && animPhase.skipPastSweep ? "none" : "streamFadeIn 0.5s ease";
          // In compare mode, tint the spine with the lane `color` (the SAME color the
          // branch scaffolding uses — see `scaffold` below) so each lane is identifiable
          // by its spine alone, not only by its branch lines. Single-forecast keeps the
          // gray past / purple future treatment (compareOn === false). `${x}44` is the
          // resting alpha; the hovered spine brightens to the full color (gray in single).
          // In the dark schemes (every theme but dusk) the resting dotted spline is
          // lifted 2 shades so its faint gray dashes read against the near-black bg;
          // the light theme keeps its tuned value, and compare-lane tint colors are
          // never touched. Hover stays on the bright grayL as before.
          const liftSpline = (hex) => (isLight ? hex : lightenHex(hex, 2));
          const spinePast = compareOn ? color : liftSpline(C.gray);
          // The single-forecast spine is neutral (no violet) and drawn as a
          // thin solid line below; dashes are kept for what is in flux (a run in progress, a
          // scenario's stream).
          const spineFuture = compareOn ? color : liftSpline(C.gray);
          const spineHover = compareOn ? color : C.grayL;
          // Dark schemes ALSO render the resting spline at a heavier opacity: the hue
          // lift alone barely moves the pixel because the dash sits at ~27% (`44`) over
          // a near-black bg — even white maxes ~rgb(73,73,80) there. Raising the resting
          // alpha is what actually makes the dotted spline read lighter. Light theme
          // keeps its tuned alphas; hover (`77`) still brightens above the resting value.
          // Gated on `!compareOn` for the same reason `liftSpline` is: in compare mode
          // spinePast/spineFuture are the lane TINT color, and bumping its alpha would
          // make compare lanes more prominent in dark themes — so compare keeps 44/22.
          const spineRest = isLight || compareOn ? "44" : "66";
          const spineFaint = isLight || compareOn ? "22" : "44";
          return (
            <>
              {(() => {
                return (
                  <>
                    {/* Main spine hover target */}
                    <div
                      onMouseEnter={() => setHoveredMainSpine(true)}
                      onMouseLeave={() => setHoveredMainSpine(false)}
                      style={{
                        position: "absolute",
                        left: pastLeft,
                        top: spineY - 7,
                        width: Math.max(0, (resScreenX || txClamped) - pastLeft + 200),
                        height: 14,
                        cursor: "default",
                        zIndex: 3,
                        pointerEvents: "auto",
                      }}
                    />
                    {/* Past spine — only when NOT awaiting (during awaiting it's part of the combined sweep below).
                      Drawn with or without past triggers: the dotted line is the question's
                      timeline, and the awaiting sweep already drew it to today with none. */}
                    {!isAwaiting && (
                      <div
                        key={`past-spine-${active?.runId || ""}`}
                        style={{
                          position: "absolute",
                          left: pastLeft,
                          top: spineY - 4,
                          width: spineW,
                          height: 8,
                          pointerEvents: "none",
                          zIndex: 3,
                          animation: streamAnim,
                          opacity: regenOpacity,
                          transition: "opacity 0.3s ease",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 4 - MAIN_SPINE_PX / 2,
                            width: "100%",
                            height: 0,
                            borderTop: `${MAIN_SPINE_PX}px solid ${hoveredMainSpine || ownBranchHovered ? spineHover : `${spinePast}${spineRest}`}`,
                            ...pastSpineMask,
                            pointerEvents: "none",
                            transition:
                              hoveredMainSpine || ownBranchHovered
                                ? "border-top-color 0.2s ease"
                                : "border-top-color 0.5s ease",
                          }}
                        />
                      </div>
                    )}
                    {/* Combined awaiting spine. NOT gated on resScreenX: a secondary
                  compare leg is created as makeRun(status:RUNNING, contract:null),
                  so it has no resolution_date (→ resScreenX null) while generating.
                  rxClamped already falls back to txClamped when resScreenX is null
                  (see above), so the sweep anchors at today — matching the old
                  compare-lane awaiting path. Gating on resScreenX hid the
                  "Generating Forecast" sweep on in-flight secondary lanes (#895 #4). */}
                    {isAwaiting &&
                      (() => {
                        const labelText = FORECASTING_LABEL;
                        const labelW = generatingLabelBox(labelText);
                        // RIGHT of the forecast line, and CENTRED IN THE GAP that the
                        // dash runs below cut for it. Those two are not in tension: gap1
                        // stops `fadeW` short of the label and gap2 resumes `fadeW` past it,
                        // so the label is centred in its own gap by construction — the gap is
                        // derived from the label, never the other way round.
                        //
                        // Do NOT move this back to the middle of the future span
                        // (`(txClamped + rxClamped) / 2`). That is the old placement, where
                        // the text competed with the triggers it was drawn between; past the
                        // forecast line that space is empty and it reads cleanly.
                        const labelLeft = rxClamped + GENERATING_LABEL_GAP;
                        const fadeW = 8;
                        const wrapperLeft = pastLeft;
                        const wrapperWidth = Math.max(0, rxClamped - pastLeft + 200);
                        const todayRel = txClamped - wrapperLeft;
                        const gap1W = Math.max(0, labelLeft - fadeW - txClamped);
                        const gap2Left = labelLeft + labelW + fadeW;
                        const gap2LeftRel = gap2Left - wrapperLeft;
                        const gap2W = Math.max(0, rxClamped - gap2Left);
                        const dashColor = `${spinePast}${hoveredMainSpine || ownBranchHovered ? "77" : spineRest}`;
                        return (
                          <React.Fragment key={`awaiting-spine-${active?.runId || ""}`}>
                            <div
                              data-telemetry-ticking=""
                              style={{
                                position: "absolute",
                                left: wrapperLeft,
                                top: spineY - 4,
                                width: wrapperWidth,
                                height: 8,
                                pointerEvents: "none",
                                zIndex: 3,
                                animation: "branchHGrow 0.8s ease-out 1.4s both",
                              }}
                            >
                              {pastW > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 4 - SPINE_DASH_HALF,
                                    width: pastW,
                                    height: 0,
                                    borderTop: `${SPINE_DASH_PX}px dashed ${dashColor}`,
                                    maskImage: pastMask,
                                    WebkitMaskImage: pastMask,
                                    pointerEvents: "none",
                                  }}
                                />
                              )}
                              {gap1W > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: todayRel,
                                    top: 4 - SPINE_DASH_HALF,
                                    width: gap1W + fadeW,
                                    height: 0,
                                    borderTop: `${SPINE_DASH_PX}px dashed ${dashColor}`,
                                    maskImage:
                                      "linear-gradient(to right, black, black calc(100% - 8px), transparent)",
                                    WebkitMaskImage:
                                      "linear-gradient(to right, black, black calc(100% - 8px), transparent)",
                                    pointerEvents: "none",
                                  }}
                                />
                              )}
                              {gap2W >= 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: gap2LeftRel - fadeW,
                                    top: 4 - SPINE_DASH_HALF,
                                    width: gap2W + fadeW + 200,
                                    height: 0,
                                    borderTop: `${SPINE_DASH_PX}px dashed ${dashColor}`,
                                    maskImage: `linear-gradient(to right, transparent, black ${fadeW}px, black ${gap2W + fadeW}px, transparent ${gap2W + fadeW + 200}px)`,
                                    WebkitMaskImage: `linear-gradient(to right, transparent, black ${fadeW}px, black ${gap2W + fadeW}px, transparent ${gap2W + fadeW + 200}px)`,
                                    pointerEvents: "none",
                                  }}
                                />
                              )}
                            </div>
                            {/* Label lives OUTSIDE the branchHGrow wrapper. That wrapper animates
                                clip-path:inset(...), which clips ALL descendants to its 8px-tall
                                box — cropping the descender of the "g" in "Generating". Rendered
                                here as a sibling in absolute screen coords (top spineY-6 = the old
                                wrapper-relative -2), with a delayed fade so it still reveals in
                                sync with the grown spine, then pulses. */}
                            <div
                              style={{
                                position: "absolute",
                                left: labelLeft,
                                // Inline: with lineHeight 1 below, the box height IS the font
                                // size, so half of it above the spine centres the glyphs on
                                // the line. Without that pin the line box is taller than the
                                // type and the text sits low.
                                top: spineY - GENERATING_LINE_H / 2,
                                lineHeight: 1,
                                width: labelW,
                                // CENTRED because this box IS the gap cut in the spine, and
                                // labelW over-reserves — left-aligned, the text butted against
                                // the incoming dashes with the whole slack left as a hole after
                                // it. The other two labels are placed to clear the bars / the
                                // branch stream, where the left edge is the meaningful edge, so
                                // they stay left-aligned.
                                display: "flex",
                                justifyContent: "center",
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                zIndex: 6,
                                animation: "branchFadeIn 0.3s ease-out 1.4s both",
                              }}
                            >
                              <LoadingLine style={GENERATING_LINE_STYLE}>{labelText}</LoadingLine>
                            </div>
                          </React.Fragment>
                        );
                      })()}
                    {/* A settled lane's timeline ends on the gold resolution
                        mark: nothing is drawn past it (no faint tail to the original date). */}
                    {!isAwaiting &&
                      !(laneResolvedTerminal && markerX != null && markerX <= txClamped) &&
                      resScreenX !== null &&
                      futW > 0 &&
                      (() => {
                        // Early resolve: the SOLID future spine ends at markerX (the actual
                        // resolve date); a fainter dashed segment continues markerX→resScreenX
                        // so the "originally forecasted" horizon stays legible. When NOT
                        // early-resolved, solidFutW === futW and the faint tail is skipped, so
                        // this renders identically to before.
                        const solidEndX = laneEarlyResolved
                          ? Math.max(-20, Math.min(vw + 20, markerX))
                          : txClamped + futW;
                        const solidFutW = Math.max(0, solidEndX - txClamped);
                        const hovBranch = hoveredBranch
                          ? laneBranches.find((b) => branchId(b) === hoveredBranch)
                          : null;
                        const forkSX = hovBranch ? dayToScreen(new Date(hovBranch.forkDate)) : null;
                        const hlW =
                          forkSX !== null
                            ? Math.max(0, Math.min(forkSX, txClamped + solidFutW) - txClamped)
                            : 0;
                        const restLeft = txClamped + hlW;
                        const restW = Math.max(0, solidFutW - hlW);
                        const futureColor = hoveredMainSpine
                          ? spineHover
                          : `${spineFuture}${spineRest}`;
                        const tailW =
                          laneEarlyResolved && !laneResolvedTerminal
                            ? Math.max(0, txClamped + futW - solidEndX)
                            : 0;
                        return (
                          <div
                            key="result"
                            style={{
                              animation: resultLineAnim,
                              opacity: regenOpacity,
                              transition: "opacity 0.3s ease",
                            }}
                          >
                            {hoveredBranch && hlW > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  left: txClamped,
                                  top: spineY - MAIN_SPINE_PX / 2,
                                  width: hlW,
                                  height: 0,
                                  borderTop: `${MAIN_SPINE_PX}px solid ${spineHover}`,
                                  pointerEvents: "none",
                                  zIndex: 3,
                                  transition: "border-top-color 0.2s ease",
                                }}
                              />
                            )}
                            <div
                              style={{
                                position: "absolute",
                                left: hoveredBranch ? restLeft : txClamped,
                                top: spineY - MAIN_SPINE_PX / 2,
                                width: (hoveredBranch ? restW : solidFutW) + 40,
                                height: 0,
                                borderTop: `${MAIN_SPINE_PX}px solid ${futureColor}`,
                                maskImage: `linear-gradient(to right, black, black ${hoveredBranch ? restW : solidFutW}px, transparent ${(hoveredBranch ? restW : solidFutW) + 40}px)`,
                                WebkitMaskImage: `linear-gradient(to right, black, black ${hoveredBranch ? restW : solidFutW}px, transparent ${(hoveredBranch ? restW : solidFutW) + 40}px)`,
                                pointerEvents: "none",
                                zIndex: 3,
                                transition: "border-top-color 0.2s ease",
                              }}
                            />
                            {tailW > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  left: solidEndX,
                                  top: spineY - SPINE_DASH_HALF,
                                  width: tailW,
                                  height: 0,
                                  borderTop: `${SPINE_DASH_PX}px dashed ${spineFuture}${spineFaint}`,
                                  pointerEvents: "none",
                                  zIndex: 3,
                                }}
                              />
                            )}
                          </div>
                        );
                      })()}
                    {/* Reforecasting overlay */}
                    {forecastingPhase === "awaiting" &&
                      shownMain.length > 0 &&
                      resScreenX !== null &&
                      futW > 0 &&
                      (() => {
                        const labelText = FORECASTING_LABEL;
                        const labelW = generatingLabelBox(labelText);
                        // RIGHT of the forecast line (see the fresh-forecast label above).
                        // Past the line there is nothing to overlap, so it sits ON the spine's
                        // centre line rather than lifted above it to clear the dashes.
                        return (
                          <div
                            data-telemetry-ticking=""
                            style={{
                              position: "absolute",
                              left:
                                (markerX ?? rxClamped) +
                                (laneDistResolved
                                  ? 8 + timelineMarkerWidth(laneDistResolved.buckets)
                                  : 0) +
                                GENERATING_LABEL_GAP,
                              top: spineY - GENERATING_LINE_H / 2,
                              lineHeight: 1,
                              width: labelW,
                              // LEFT, deliberately — unlike the spine label above, this one has no
                              // dash gap around it to be centred in. Its left edge is anchored just
                              // past the outcome bars, so the left edge is what carries the meaning;
                              // centring would push the text right by half the width over-estimate,
                              // away from the bars it is placed to sit beside. Same reasoning as the
                              // branch-stream label further down. See #4207.
                              display: "flex",
                              whiteSpace: "nowrap",
                              pointerEvents: "none",
                              zIndex: 6,
                            }}
                          >
                            <LoadingLine style={GENERATING_LINE_STYLE}>{labelText}</LoadingLine>
                          </div>
                        );
                      })()}
                  </>
                );
              })()}
            </>
          );
        })()}

      {/* Per-lane rowId + anim: lane 0 (single + compare primary) is the "main"
          spine and runs the global reveal animPhase; secondary compare lanes use
          rowId "compare-<laneIndex>" (so their slideCache / collapse / promote
          caches don't collide with the primary's "main" namespace) and pass null
          anim (the global reveal belongs to the primary). Matches the pre-Task-3
          renderRow(...,"main") vs renderRow(...,"compare-N") split. */}
      {/* Events wrapper. regenOpacity is now always 1 (reforecast no longer dims),
          so opacity:1 creates NO stacking context and normal z-layering is unchanged.
          Kept as a wrapper so the opacity hook stays in one place if a fade returns. */}
      <div style={{ opacity: regenOpacity, transition: "opacity 0.3s ease" }}>
        {renderRow(
          // Distribution runs — multi-value always, binary under All Multiway: tag every
          // trigger `_neutral` so the render primitives draw it white + on the spine
          // baseline (no up/down direction, no occurs/not-occurs valence color).
          // The glyph itself needs no extra tagging: a neutral trigger renders a pie filled
          // to `p_event` whenever it has one (#4039), which a binary trigger always does, so
          // its probability and its impact-driven size both survive the loss of valence.
          withScenarioOutcomes(
            laneDist ? shownMain.map((e) => ({ ...e, _neutral: true })) : shownMain,
            childrenOf(root?.runId),
            premiseViews,
            root?.events,
          ),
          spineY,
          color,
          false,
          false,
          config.laneIndex === 0 ? animPhase : null,
          0,
          config.laneIndex === 0 ? "main" : "compare-" + config.laneIndex,
          undefined,
          undefined,
          undefined,
          null,
          markerX == null
            ? null
            : {
                lineX: markerX,
                ...(laneDistResolved &&
                  timelineMarkerFootprint(markerX, laneDistResolved.buckets.length)),
              },
        )}
      </div>

      {/* === DISTRIBUTION MARKER (multi-value, and binary under All Multiway) === */}
      {/* In place of the pie below we show a compact per-bucket glyph at the marker.
          Mutually exclusive with the pie block, which is skipped whenever `laneDist` is
          set. Once resolved the bars collapse to 100/0 with a check mark and gold on the
          bin that WON — including a binary question that settled on "does not occur".
          Clicking opens the full "Outcome distribution" report. */}
      {resScreenX !== null &&
        markerX != null &&
        markerX > -40 &&
        markerX < vw + 40 &&
        laneDistResolved &&
        (() => {
          // Same reveal gate the pie applies — the marker lands after the triggers do.
          // Under All Multiway only: with the flag off, a multi-value marker keeps its
          // original ungated appearance, so nothing about today's behaviour moves.
          const resRevealed = !animPhase || animPhase.revealCount >= animPhase.totalEvents;
          if (allMultiway && !resRevealed) return null;
          return (
            <>
              <MultiwayMarker
                buckets={laneDistResolved.buckets}
                kind={laneDistResolved.kind}
                resolvedIndex={laneDistResolved.resolvedIndex}
                badge={laneDistResolved.badge}
                preview={hoverPreview}
                // Which verdict settled it — the marker colours a binary "does not occur"
                // row grey rather than gold, matching its forecast-manager card.
                // Carries the onboarding tour's resolution anchor, which the pie block below
                // owns when it draws instead.
                tourSpotlight={config.laneIndex === 0 ? "resolution" : undefined}
                markerX={markerX}
                spineY={spineY}
                // Dawn takes the theme accent; the dark schemes keep purple (his #4121).
                C={C}
                onOpen={() => openReport(config.laneIndex === 0 ? true : root)}
                {...ui("timeline.resolution-marker", root.runId)}
                // Rich hover popup, same as the binary resolution pie (keyed on the SAME
                // `res-${runId}` hov slot — the two markers are mutually exclusive, now via
                // `laneDist`). Restores the "final forecast" popup on the distribution's
                // marker, which previously only had a native title.
                onHoverEnter={(e) => {
                  setHov(`res-${root.runId}`);
                  setHovPos({ x: e.clientX, y: e.clientY });
                }}
                onHoverMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
                onHoverLeave={() => {
                  setHov(null);
                  // Leaving the marker entirely must release the plot too: the per-row
                  // leave fires when moving BETWEEN rows, so it cannot be the only reset.
                  setHoveredBin?.(null);
                }}
                // Hovering an outcome row retraces the probability plot on THAT outcome.
                // Primary lane only — a compare lane's bars must not redraw the primary
                // lane's plot, which is the only one on screen.
                onBinHover={config.laneIndex === 0 ? (label) => setHoveredBin?.(label) : undefined}
              />
              {hov === `res-${root.runId}` &&
                (() => {
                  // CALIBRATION. The snapshot bins forecasts by probability and reports what
                  // share OCCURRED. Binary states it on the P(YES) axis; a multi-value space
                  // states it on the LEADING BUCKET'S MASS.
                  //
                  // THE INVARIANT: `calibP` must feed BOTH the bin lookup and the tooltip's
                  // `probability`, because the displayed range is derived from `probability`
                  // while the counts come from the bin. Two different numbers print one bin's
                  // counts under another bin's range — the bug that reverted this once.
                  //
                  // Hence also `selectLead` and not `winnerOf`: same bucket, but `winnerOf`
                  // rounds for display and rounding can cross a bin edge. And `laneDist`, not
                  // `laneDistResolved`: the latter is 100/0 once a verdict lands, and
                  // calibrating a certainty is meaningless.
                  //
                  // CAVEAT: the bins behind this are binary-resolved and not stratified by
                  // support size, so a 3-way and a 10-way lead read against the same curve.
                  // The line stays true (it describes the historical population at that
                  // level, not this question), but a real top-label curve needs a producer
                  // that does not exist yet.
                  const pYes = active?.contract?.probability;
                  const leadMass =
                    laneDist && laneDist.kind !== "binary"
                      ? selectLead(laneDist.buckets, null).lead?.p
                      : null;
                  const calibP =
                    laneDist?.kind === "binary"
                      ? typeof pYes === "number"
                        ? pYes
                        : null
                      : typeof leadMass === "number"
                        ? leadMass
                        : null;
                  const calibCell =
                    calibP != null
                      ? lookupCalibrationBin(calibration, CALIBRATION_MODEL_ID, calibP)
                      : null;
                  return (
                    <Tooltip
                      ev={{
                        title: active?.contract?.question || active?.contract?.title || "",
                        description: "",
                        isPast: false,
                        isResolution: true,
                        date: active?.contract?.resolution_date,
                        // The calibration block is the ONLY reader of this on an
                        // `isResolution` tooltip (the "Forecasted: N%" line is inside the
                        // `!isResolution` branch), so carrying the lead mass here on a
                        // multi-value forecast adds the line and changes nothing else.
                        probability: calibP,
                        // Stated large under the question, above the calibration line.
                        _winner: winnerReadout,
                        calibration: calibCell,
                        _calibrationEnabled: calibCell?.resolvedTrue != null,
                      }}
                      x={hovPos.x}
                      y={hovPos.y}
                      zoom={uiZoom}
                    />
                  );
                })()}
            </>
          );
        })()}

      {/* === RESOLUTION MARKER === */}
      {/* On-screen test keys off markerX — where the pie/glyph ACTUALLY renders — not
          resScreenX (the horizon). For an early-resolved forecast the pie sits at the
          actual resolve date (markerX = resolvedScreenX), far LEFT of a distant horizon;
          testing resScreenX culled the whole marker when zooming in pushed the horizon
          off-screen even though the pie was still in view. markerX === resScreenX when
          not early-resolved, so the normal timeline is unchanged. */}
      {resScreenX !== null &&
        markerX != null &&
        markerX > -40 &&
        markerX < vw + 40 &&
        active?.contract?.probability != null &&
        // Stand down when this lane draws a distribution instead (multi-value, or binary
        // under All Multiway) — the two markers occupy the same spot.
        !laneDist &&
        (() => {
          const resRevealed = !animPhase || animPhase.revealCount >= animPhase.totalEvents;
          if (!resRevealed) return null;
          // Verdict state is hoisted to the lane top so the distribution marker shares
          // it — markerX / laneEarlyResolved likewise. Verdict pie/glyph/label render at
          // markerX (the actual resolve date when early); the purple forecast line stays
          // at resScreenX.
          const {
            isResolvedYes,
            isResolvedNo,
            isResolvedTie,
            hasTerminalVerdict,
            effectiveUnresolved,
            isVoided,
            showVerdictVisual,
          } = laneVerdict;
          const rawProb = active.contract.probability;
          const resProb =
            showVerdictVisual && isResolvedYes
              ? 1
              : showVerdictVisual && isResolvedNo
                ? 0
                : showVerdictVisual && isResolvedTie
                  ? 0.5
                  : rawProb;
          const resIconSz = RES_SZ + 14;
          // Yes at exactly 50%, as the readout names it.
          const resAbove = resProb >= 0.5;
          // The side the readout and its tooltip lead with: the larger one.
          const { p: resLeadP, label: resLeadSide } = leadingSide(resProb);
          const MAX_TRIGGER_R = 18;
          const channelOffset = STREAM_HALF * 0.55;
          const iconCenterAt1 = spineY - channelOffset - MAX_TRIGGER_R;
          const iconCenterAt0 = spineY + channelOffset + MAX_TRIGGER_R;
          const resIconCenterY =
            resProb >= 0.5
              ? spineY + (resProb - 0.5) * 2 * (iconCenterAt1 - spineY)
              : spineY + (0.5 - resProb) * 2 * (iconCenterAt0 - spineY);
          const resIconTop = resIconCenterY - resIconSz / 2;
          // Compare mode tints the resolution marker (pie + %label) with the lane color so
          // a secondary (e.g. green) lane doesn't render a purple marker against its
          // lane-colored spine/scaffolding. Single-forecast keeps purple; the red/blue-team
          // override and the resolved-tie purpleL are unchanged.
          const resCol = isResolvedTie
            ? C.purpleL
            : redBlueTeam
              ? resAbove
                ? C.blueTeam
                : C.redTeam
              : compareOn
                ? color
                : C.purple;
          // Guard: resolutionMomentLocal returns null for an unparseable
          // resolution_date, and `null < effectiveToday` coerces to `0 < …` = true,
          // which would wrongly force C.past. Only compare a valid moment.
          const _resMoment = resDate ? resolutionMomentLocal(resDate, active?.question) : null;
          const resBg =
            _resMoment && !isNaN(_resMoment.getTime()) && _resMoment < effectiveToday
              ? C.past
              : C.future;
          return (
            <>
              {/* Primary lane (laneIndex===0) opens the active report via `true`; secondary/compare lanes open their own root run's report. */}
              <div
                data-tour-spotlight={config.laneIndex === 0 ? "resolution" : undefined}
                data-clears-question=""
                {...ui("timeline.resolution-marker", root.runId)}
                onMouseEnter={(e) => {
                  setHov(`res-${root.runId}`);
                  setHovPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHov(null)}
                onClick={() => openReport(config.laneIndex === 0 ? true : root)}
                style={{
                  position: "absolute",
                  left: markerX - resIconSz / 2 - 2,
                  top: resIconTop,
                  zIndex: 30,
                  cursor: "pointer",
                  animation: animPhase ? "popIn 0.5s ease-out forwards" : "none",
                }}
              >
                <div
                  style={{
                    padding: 2,
                    borderRadius: "50%",
                    background: resBg,
                    position: "relative",
                  }}
                >
                  <ResolutionIcon
                    p={resProb}
                    sz={resIconSz}
                    color={resCol}
                    bg={resBg}
                    pulseDur={ctx.misc.resPulseDur}
                  />
                  {showVerdictVisual && (isResolvedYes || isResolvedNo) && (
                    <svg
                      width={resIconSz}
                      height={resIconSz}
                      style={{ position: "absolute", left: 2, top: 2, pointerEvents: "none" }}
                    >
                      <text
                        x={resIconSz / 2}
                        y={resIconSz / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={Math.max(12, resIconSz / 2 - 2)}
                        fill={C.white}
                        stroke={C.white}
                        strokeWidth={1}
                        paintOrder="stroke"
                        fontFamily="IBM Plex Mono, monospace"
                        fontWeight="bold"
                      >
                        {isResolvedYes ? "✓" : "✕"}
                      </text>
                    </svg>
                  )}
                </div>
              </div>
              <div
                data-clears-question=""
                style={{
                  position: "absolute",
                  left: markerX + resIconSz / 2 + 6,
                  top: resIconCenterY + 2,
                  transform: "translateY(-50%)",
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  zIndex: 30,
                }}
              >
                {showVerdictVisual && hasTerminalVerdict ? (
                  <div
                    style={{
                      fontSize: 15,
                      color: resCol,
                      fontWeight: 600,
                      lineHeight: 1.1,
                      letterSpacing: 0,
                    }}
                  >
                    {isResolvedYes ? "Occurred" : isResolvedNo ? "Did not occur" : "Tied"}
                  </div>
                ) : showVerdictVisual && (isVoided || effectiveUnresolved) ? (
                  <div
                    style={{
                      fontSize: 15,
                      color: C.grayL,
                      fontWeight: 600,
                      lineHeight: 1.1,
                      letterSpacing: 0,
                    }}
                  >
                    {/* `isVoided` first so it wins WHEN both are set — a void stamped
                        after the resolution moment sets both; one stamped before it
                        sets only `isVoided`. Same grey as unresolved — neither is a
                        verdict, so the word carries the distinction, not the colour. */}
                    {isVoided ? "Voided" : "No answer yet"}
                  </div>
                ) : (
                  // The larger side leads (the header carries the hero number; this is the
                  // compact readout at the line's end), the other side muted beneath it.
                  <div data-answer-kind="binary" style={{ lineHeight: 1 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <span
                        className="sy-num"
                        style={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: compareOn ? resCol : C.white,
                        }}
                      >
                        {fmtPct(resLeadP)}%
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: C.white }}>
                        {resLeadSide}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 6,
                        marginTop: 5,
                        fontSize: 12,
                        color: C.gray,
                      }}
                    >
                      <span className="sy-num">{fmtPct(1 - resLeadP)}%</span>
                      <span>{resLeadSide === "Yes" ? "No" : "Yes"}</span>
                    </div>
                  </div>
                )}
                {compareOn && (
                  <div
                    style={{
                      fontSize: 11,
                      color: resCol,
                      letterSpacing: 0,
                      opacity: 0.85,
                      marginTop: 4,
                    }}
                  >
                    {forecasterDebugLabel(rootEngine(active))}
                  </div>
                )}
              </div>
              {hov === `res-${root.runId}` &&
                (() => {
                  const qText = active?.contract?.question || active?.contract?.title || "";
                  const expl =
                    active?.serverExplanation || active?.serverContext?.explanation || "";
                  const stance =
                    resProb > 0.7
                      ? "strongly favors Yes"
                      : resProb > 0.55
                        ? "leans toward Yes"
                        : resProb > 0.45
                          ? "remains highly uncertain"
                          : resProb > 0.3
                            ? "leans toward No"
                            : "strongly favors No";
                  const ctxText = active?.contract?.background || "";
                  const clean = expl
                    .replace(/^#{1,6}\s*/gm, "")
                    .replace(/\*{1,2}([^*]+)\*{1,2}/g, "$1")
                    .replace(/^\d+\.\s*/gm, "")
                    .replace(/^[-•]\s*/gm, "")
                    .replace(/\b(Reasoning|Step)\s*\d+[.:]?\s*/gi, "");
                  const paras = clean
                    .split(/\n\n+/)
                    .map((p) => p.trim())
                    .filter((p) => p.length > 40);
                  const conclusionPara =
                    paras
                      .filter((p) =>
                        /probability|calibrat|estimate|forecast|overall|weigh|balance|assessment|final|conclude/i.test(
                          p,
                        ),
                      )
                      .slice(-1)[0] || "";
                  const conclusionSentences = conclusionPara
                    .split(/[.!?]\s+/)
                    .filter((s) => s.length > 20)
                    .slice(0, 3)
                    .join(". ");
                  const ctxSummary = ctxText
                    ? ctxText
                        .split(".")
                        .filter((s) => s.trim().length > 15)
                        .slice(0, 2)
                        .join(".") + "."
                    : "";
                  const summaryText = conclusionSentences
                    ? conclusionSentences + (conclusionSentences.endsWith(".") ? "" : " ")
                    : ctxSummary ||
                      `At ${fmtPct(resLeadP)}% ${resLeadSide}, our analysis ${stance}. The balance of evidence and current trajectory inform this assessment.`;
                  const calibCell = lookupCalibrationBin(
                    calibration,
                    CALIBRATION_MODEL_ID,
                    resProb,
                  );
                  return (
                    <Tooltip
                      ev={{
                        title: qText,
                        description: "",
                        _summaryText: summaryText.slice(0, 400),
                        isPast: false,
                        probability: resProb,
                        isResolution: true,
                        date: resDate,
                        calibration: calibCell,
                        _calibrationEnabled: calibCell?.resolvedTrue != null,
                      }}
                      x={hovPos.x}
                      y={hovPos.y}
                      zoom={uiZoom}
                    />
                  );
                })()}
            </>
          );
        })()}

      {/* === BRANCH SUBTREE === */}
      {(() => {
        const laneSpineY = spineY;
        const laneColor = color;
        const laneRoot = root;
        const branchYsLocal = branchYs;
        const resDateB = laneRoot?.contract?.resolution_date;
        const scaffold = compareOn ? laneColor : C.gray;
        const branchIdToIdx = {};
        laneBranches.forEach((b, i) => {
          branchIdToIdx[branchId(b)] = i;
        });
        const connectorTops = laneBranches.map((b) => {
          const pid = branchParentId(b);
          const parentIdx = pid != null ? branchIdToIdx[pid] : undefined;
          return parentIdx != null ? branchYsLocal[parentIdx] : laneSpineY;
        });
        const chainHoveredFlag = laneBranches.map((b) => hoveredBranch === branchId(b));
        return laneBranches.map((branch, bi) => {
          const bSY = branchYsLocal[bi];
          const connectorTopY = connectorTops[bi];
          const forkDate = new Date(branch.forkDate);
          const forkDateMs = forkDate.getTime();
          const isValidForkDate = !isNaN(forkDateMs);
          // Parse the branch resolution ONCE and guard it: a present-but-invalid
          // resolution_date must NOT yield NaN (which would poison the Math.min clamp
          // below and the branch layout). Invalid → no resolution line (Infinity/null),
          // exactly like an absent date. resolutionMomentLocal (not new Date) so the
          // fork-clamp lands on the same end-of-day line as the branch resolution marker.
          const bResMoment = resDateB ? resolutionMomentLocal(resDateB, laneRoot?.question) : null;
          const bResValid = !!bResMoment && !isNaN(bResMoment.getTime());
          const resDateMs = bResValid ? bResMoment.getTime() : Infinity;
          const bRSX = bResValid ? dayToScreen(bResMoment) : null;
          // A fork/trigger must never render to the RIGHT of the branch resolution line
          // — e.g. a custom-event fork placed past the line, or a stored forkDate a
          // past-resolution timeline would otherwise push out toward today. Clamp its
          // screen position onto the line (no-op for a normal fork before the line).
          // Spine + branch-spine triggers are capped upstream in eventAnchorMs.
          const fSX = bRSX != null ? Math.min(dayToScreen(forkDate), bRSX) : dayToScreen(forkDate);
          const bIdVal = branchId(branch);
          const bProb = branchProbability(branch);
          // The distribution this branch draws instead of its pie — same rule as the
          // lane's `laneDist`: multi-value always, binary only under All Multiway.
          const bDist = extractDistribution(branch?.contract, {
            allMultiway,
            probability: bProb,
          });
          const bLoading = branchLoading(branch);
          // This branch's PREMISE state (lib/forecast/branchPremise.ts). A
          // CONTRADICTED premise makes the branch terminal: the world went the other
          // way, so its conditional probability is no longer a forecast of anything
          // and the marker below states what actually happened instead. Every other
          // state (pending / watching / confirmed) renders exactly as before —
          // a confirmed branch is still live, just with its premise now a fact.
          const bPremise = premiseViews ? premiseViews[bIdVal] : null;
          // EVERY terminal outcome collapses the branch's distribution and its downstream
          // triggers: whatever settled it, there is no conditional forecast left to draw,
          // and the terminal panel takes the marker slot instead. The routes in are a
          // graded premise, a premise that can never settle, and the ROOT's own question
          // resolving out from under a still-pending one.
          // WHICH terminal statement this branch shows, or null while it is still live.
          // The five-state choice lives in lib/ (`branchTerminalState`) rather than inline:
          // it has two independent inputs and one non-obvious ordering rule — the branch's
          // own premise outranks the trunk — and getting that wrong is not a cosmetic slip.
          // Written as a ternary here, an INCONCLUSIVE verdict fell through to "superseded",
          // whose panel announces "The main forecast has resolved" about a trunk that had
          // not resolved at all.
          const bTerminalState = branchTerminalState({
            premiseState: bPremise?.state,
            verdictStatus: bPremise?.verdictStatus,
            rootHasTerminalVerdict: laneVerdict.hasTerminalVerdict,
          });
          // The one gate for "draw the terminal panel, not a live forecast".
          const bTerminal = bTerminalState !== null;
          // What actually happened to the TRIGGER, which on a contradicted branch is
          // by definition the opposite of what the user assumed.
          // A dead branch's scaffolding reads dead. One dimmed neutral grey for BOTH
          // the vertical connector and the horizontal stream, overriding the lane
          // colour and the hover brighten — a contradicted branch that lights back up
          // under the cursor reads as live again, which is the one thing this state
          // exists to deny. The line still runs its full width (the premise verdict
          // sits at the far end), it just stops claiming to be a forecast.
          const bDeadScaffold = `${C.gray}55`;
          // Which branches get that treatment: every terminal state except confirmed — a
          // confirmed branch's assumption held, so greying it would deny a path that
          // actually happened. The others join because "no longer live" is exactly the
          // claim the grey makes.
          const bScaffoldDead = branchScaffoldDead(bTerminalState);
          const branchEvtsRaw = (branch.events || [])
            .filter((e) => {
              if (!e.date) return false;
              const ed = new Date(e.date);
              if (isNaN(ed.getTime())) return false;
              const md = e.mean_date ? new Date(e.mean_date) : null;
              const visMs = md && !isNaN(md.getTime()) ? md.getTime() : ed.getTime();
              if (isValidForkDate && visMs <= forkDateMs) return false;
              const visScreen = md && !isNaN(md.getTime()) ? dayToScreen(md) : dayToScreen(ed);
              if (visScreen <= fSX + 1) return false;
              return true;
            })
            .map((e) => {
              if (!resDateB) return e;
              const ed = new Date(e.date);
              if (ed.getTime() > resDateMs)
                return { ...e, date: resDateB, date_end: null, mean_date: null };
              const out = { ...e };
              if (e.date_end && new Date(e.date_end).getTime() > resDateMs) out.date_end = resDateB;
              if (e.mean_date && new Date(e.mean_date).getTime() > resDateMs)
                out.mean_date = resDateB;
              return out;
            })
            .sort((a, b) => new Date(a.date) - new Date(b.date));
          // The fork node, as the branch lane draws it — anchored to the branch's stored
          // forkDate (the same date `fSX` draws the corner at) with any window kept.
          // See buildForkNode for why the windowed case needs an explicit anchor.
          const forkEvt = buildForkNode(branch, resDateMs, resDateB);
          const hasForkInEvts = forkEvt && branchEvtsRaw.some((e) => e.id === forkEvt.id);
          const allBranchEvts =
            forkEvt && !hasForkInEvts
              ? [forkEvt, ...branchEvtsRaw]
              : hasForkInEvts
                ? branchEvtsRaw.map((e) => (e.id === forkEvt.id ? forkEvt : e))
                : branchEvtsRaw;
          const isCollapsed = !!branch.collapsed;
          const bE = isCollapsed && forkEvt ? [forkEvt] : allBranchEvts;
          // Lane-scoped via the branch runId (not the per-lane index `bi`): in
          // compare mode two lanes commonly have a branch at the same index, and a
          // bare `rbr${bi}` would let one branch's hover satisfy `hov === brh` in
          // every lane → stacked tooltips. Keeps the `rbr` prefix so App.jsx's
          // `hov.startsWith("rbr")` exclusions still apply.
          const brh = `rbr${bIdVal}`;
          const bAnim = branchAnim && branchAnim.branchId === bIdVal ? branchAnim : null;
          const bHovered = hoveredBranch === bIdVal;
          const bStreamLeft = fSX;
          const bStreamRight = bRSX !== null ? bRSX : bStreamLeft;
          const isNewBranch = !!bAnim;
          const triggerIsYes = forkEvt ? forkEvt.direction !== "no" : true;
          const triggerColor = redBlueTeam ? (triggerIsYes ? C.blueTeam : C.redTeam) : laneColor;
          const triggerCtx = forkEvt
            ? {
                branchId: bIdVal,
                color: triggerColor,
                // What this branch asserted, so the fork node's label can say it — two
                // branches off one trigger otherwise share an identical label.
                sense: premiseSenseOf({ sense: branch.forkSense, forkEvent: branch.forkEvent }),
                isCollapsed,
                onClearBranch: () => removeBranch(bIdVal),
                onToggleCollapse: () => toggleBranchCollapsed(bIdVal),
                // Premise WATCHING — the fork's window has opened but no decisive
                // verdict has landed. Read by the shared trigger-glyph renderer
                // (TriggerNode), which draws the ring as a heavier long dash instead
                // of fine dots, and by the trigger tooltip, which says so in words.
                // Without a distinct state here, a branch sitting live past its own
                // trigger date looks like a bug rather than a question still open.
                //
                // AND `gradable`: watching means "a verdict is on its way", so it must
                // not be claimed for a fork with no contract behind it (a typed trigger
                // whose preprocess failed). That premise is pending forever, and saying
                // "pending resolution" about it states something false.
                watching: !!bPremise?.watching && !!bPremise?.gradable,
                // Premise SETTLED (confirmed or contradicted alike) — the node
                // renders as a past trigger stating which way it went. The node
                // says only what happened; whether the branch is still live is
                // expressed by the marker + line styling, not by the node.
                premiseResolved:
                  bPremise?.state === "confirmed" || bPremise?.state === "contradicted",
                premiseOccurred: bPremise?.verdictStatus === "resolved_yes",
                // The window has closed with no ruling: a "?" on the watching ring.
                overdue: !!bPremise?.overdue && !!bPremise?.gradable,
                // Settled without an answer (a tie, a void, or nothing able to rule): a "?"
                // on a solid ring.
                premiseUndetermined: bPremise?.state === "indeterminate",
                // A written trigger contract, which Update now and the scheduled resolver
                // ask about again.
                recheckable: !!bPremise?.resolvableKey,
              }
            : null;
          return (
            <div
              key={bIdVal}
              {...surface("timeline.branch", bIdVal)}
              {...(bAnim ? { "data-branch-anim-active": "" } : {})}
            >
              {/* Vertical dashed connector (replaced by the glow peel-away when on) */}
              {!glowEnabled &&
                (() => {
                  const SPINE_THICK = 0;
                  const vTop = connectorTopY + SPINE_THICK;
                  const ch = Math.max(0, bSY - vTop);
                  const isOnChain = chainHoveredFlag[bi];
                  const strokeColor = bScaffoldDead
                    ? bDeadScaffold
                    : isOnChain
                      ? compareOn
                        ? scaffold
                        : C.grayL
                      : `${scaffold}55`;
                  const HIT_W = 14;
                  return (
                    <div
                      onMouseEnter={() => setHoveredBranch(bIdVal)}
                      onMouseLeave={() => setHoveredBranch(null)}
                      style={{
                        position: "absolute",
                        left: fSX - HIT_W / 2,
                        top: vTop,
                        width: HIT_W,
                        height: ch,
                        pointerEvents: "auto",
                        zIndex: 4,
                        cursor: "default",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: HIT_W / 2,
                          top: 0,
                          width: 0,
                          height: "100%",
                          borderLeft: `${SPINE_DASH_PX}px dashed ${strokeColor}`,
                          pointerEvents: "none",
                          transition: isOnChain
                            ? "border-left-color 0.2s ease"
                            : "border-left-color 0.5s ease",
                          ...(isNewBranch
                            ? { animation: "branchVGrow 0.6s ease-out forwards" }
                            : {}),
                        }}
                      />
                    </div>
                  );
                })()}
              {/* Branch stream: dashed center + label inline + channel lines
                  (replaced by the glow branch line + its own label when on) */}
              {!glowEnabled &&
                bStreamRight > bStreamLeft &&
                (() => {
                  // No "Branch N" label. The number named nothing a user chose — the branch
                  // is identified by its trigger and its own line — and it sat in a gap cut
                  // out of the dashed stream, so removing it also closes that gap. Only the
                  // transient generating status remains, placed like the main lane's.
                  const fullLabel = bLoading ? FORECASTING_LABEL : "";
                  const CHAR_W = 6.2;
                  const sidePad = Math.round(CHAR_W * 2);
                  // Zero when there is no label. The container keeps pointerEvents/zIndex
                  // for the branch-hover affordance, so padding an EMPTY string still left a
                  // ~24px invisible strip past the end of the stream that answered the mouse.
                  const labelW = fullLabel ? generatingLabelBox(fullLabel) : 0;
                  // Past the RIGHT end of the branch stream, mirroring the main lane. With
                  // nothing sitting in the stream any more, the two gaps that used to cut the
                  // dashes around the label collapse (gap1 spans the whole stream, gap2 is
                  // empty) — the branch line runs unbroken.
                  const labelLeft =
                    (bRSX ?? bStreamRight) +
                    (bDist ? 8 + timelineMarkerWidth(bDist.buckets) : 0) +
                    GENERATING_LABEL_GAP;
                  const fadeW = sidePad;
                  const gap1W = Math.max(0, bStreamRight - bStreamLeft);
                  const gap2W = 0;
                  return (
                    <>
                      {gap1W > 0 && (
                        <div
                          onMouseEnter={() => setHoveredBranch(bIdVal)}
                          onMouseLeave={() => setHoveredBranch(null)}
                          style={{
                            position: "absolute",
                            left: bStreamLeft,
                            top: bSY - 7,
                            width: gap1W + fadeW,
                            height: 14,
                            borderTop: "none",
                            pointerEvents: "auto",
                            zIndex: 3,
                            cursor: "default",
                            ...(isNewBranch
                              ? { animation: "branchHGrow 1.2s ease-out 0.6s both" }
                              : {}),
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 7 - SPINE_DASH_HALF,
                              width: "100%",
                              height: 0,
                              borderTop: `${SPINE_DASH_PX}px dashed ${
                                bScaffoldDead
                                  ? bDeadScaffold
                                  : bHovered
                                    ? compareOn
                                      ? scaffold
                                      : C.grayL
                                    : `${scaffold}44`
                              }`,
                              maskImage: `linear-gradient(to right, black, black calc(100% - ${fadeW}px), transparent)`,
                              WebkitMaskImage: `linear-gradient(to right, black, black calc(100% - ${fadeW}px), transparent)`,
                              pointerEvents: "none",
                              transition: bHovered
                                ? "border-top-color 0.2s ease"
                                : "border-top-color 0.5s ease",
                            }}
                          />
                        </div>
                      )}
                      <div
                        data-telemetry-ticking={bLoading ? "" : undefined}
                        onMouseEnter={() => setHoveredBranch(bIdVal)}
                        onMouseLeave={() => setHoveredBranch(null)}
                        style={{
                          position: "absolute",
                          left: labelLeft,
                          top: bSY - GENERATING_LINE_H / 2,
                          lineHeight: 1,
                          width: labelW,
                          display: "flex",
                          opacity: bHovered ? 1 : 0.8,
                          whiteSpace: "nowrap",
                          pointerEvents: "auto",
                          zIndex: 6,
                          cursor: "default",
                          ...(isNewBranch
                            ? { animation: "branchFadeIn 0.6s ease-out 1.0s both" }
                            : {}),
                        }}
                      >
                        {bLoading && (
                          <LoadingLine style={GENERATING_LINE_STYLE}>
                            {FORECASTING_LABEL}
                          </LoadingLine>
                        )}
                      </div>
                      {gap2W > 0 && (
                        <div
                          onMouseEnter={() => setHoveredBranch(bIdVal)}
                          onMouseLeave={() => setHoveredBranch(null)}
                          style={{
                            position: "absolute",
                            left: bStreamRight,
                            top: bSY - 7,
                            width: gap2W + fadeW,
                            height: 14,
                            borderTop: "none",
                            pointerEvents: "auto",
                            zIndex: 3,
                            cursor: "default",
                            ...(isNewBranch
                              ? { animation: "branchHGrow 1.2s ease-out 0.6s both" }
                              : {}),
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 7 - SPINE_DASH_HALF,
                              width: "100%",
                              height: 0,
                              borderTop: `${SPINE_DASH_PX}px dashed ${bHovered ? (compareOn ? scaffold : C.grayL) : `${scaffold}44`}`,
                              maskImage: `linear-gradient(to right, transparent, black ${fadeW}px, black)`,
                              WebkitMaskImage: `linear-gradient(to right, transparent, black ${fadeW}px, black)`,
                              pointerEvents: "none",
                              transition: bHovered
                                ? "border-top-color 0.2s ease"
                                : "border-top-color 0.5s ease",
                            }}
                          />
                        </div>
                      )}
                    </>
                  );
                })()}
              {/* Branch events */}
              {renderRow(
                // A distribution branch's triggers are neutral too — white, on-spine, no
                // red/green (same as the primary lane).
                withScenarioOutcomes(
                  bDist ? bE.map((e) => ({ ...e, _neutral: true })) : bE,
                  childrenOf(bIdVal),
                  premiseViews,
                  branch.events,
                ),
                bSY,
                laneColor,
                false,
                false,
                bAnim,
                0,
                "branch-" + bIdVal,
                undefined,
                forkEvt && triggerCtx ? { evId: forkEvt.id, ctx: triggerCtx } : undefined,
                fSX,
                forkDate.getTime(),
                bRSX == null
                  ? null
                  : {
                      lineX: bRSX,
                      // Only while the bars draw: the terminal panel takes their slot.
                      ...(!isCollapsed &&
                        !bTerminal &&
                        bDist &&
                        timelineMarkerFootprint(bRSX, bDist.buckets.length)),
                    },
              )}
              {/* Branch distribution marker — the per-bucket glyph at the branch resolution
                  point (the reforecast's "final output" conditioned on the trigger), in place
                  of the pie below. A multi-value branch has no single P(YES) (bProb == null)
                  so it always lands here; a binary one only under All Multiway. Branches carry
                  no verdict on their own QUESTION, but any terminal premise collapses this
                  marker, as does the ROOT's question resolving out from under a pending one
                  — both via `bTerminal` (see branchTerminalState). */}
              {!isCollapsed &&
                !bTerminal &&
                bRSX !== null &&
                bRSX > -40 &&
                bRSX < vw + 40 &&
                bDist &&
                (!bAnim || bAnim.keepResult || bAnim.revealCount >= bAnim.totalEvents) && (
                  <MultiwayMarker
                    buckets={bDist.buckets}
                    markerX={bRSX}
                    spineY={bSY}
                    C={C}
                    onOpen={() => openReport(branch)}
                    {...ui("timeline.resolution-marker", branch.runId)}
                    // Hover wiring, on the SAME `brh` key the branch's pie uses, so the
                    // branch tooltip below fires for either shape.
                    //
                    // It was missing entirely: the pie element (rendered when the branch has
                    // no distribution) carried the handlers, and this marker — its replacement
                    // whenever the branch IS a distribution — carried none. So a multiway
                    // branch had no hover box at all, and passing `_winner` to the tooltip
                    // could not help, because nothing ever set `hov` to show it.
                    onHoverEnter={(e) => {
                      setHov(brh);
                      setHovPos({ x: e.clientX, y: e.clientY });
                    }}
                    onHoverMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
                    onHoverLeave={() => setHov(null)}
                  />
                )}

              {/* Branch premise verdict — replaces BOTH markers above when the
                  branch's premise was contradicted. Rendered for the pie and the
                  multiway paths alike: the premise is a yes/no fact regardless of
                  the parent's outcome space, so it must not depend on which marker
                  the live branch would otherwise have drawn. */}
              {/* Terminal-branch panel — replaces the distribution for a SETTLED premise
                  (either outcome) or a SUPERSEDED branch (the trunk resolved first). Sits
                  at the resolution marker, vertically centred on the branch stream,
                  because that is the slot the bars occupied. */}
              {!isCollapsed && bTerminal && bRSX !== null && bRSX > -40 && bRSX < vw + 40 && (
                <TerminalSlot
                  {...surface("timeline.branch-terminal", bIdVal)}
                  // Beside the resolution marker, pulled left to stay in the open timeline: a
                  // panel under anything else loses its Clear scenario click to it.
                  left={Math.max(
                    8,
                    Math.min(bRSX + 10, vw - rightInset - BRANCH_TERMINAL_PANEL_OUTER_W - 12),
                  )}
                  centerY={bSY}
                >
                  <BranchTerminalPanel
                    // Chosen by `branchTerminalState`, which owns the ordering: what
                    // happened to this branch's own premise beats what happened to the
                    // trunk, so a graded branch keeps stating its own outcome even under a
                    // resolved root.
                    state={bTerminalState}
                    incorporated={!!bPremise?.incorporated}
                    C={C}
                    // Live wherever the premise has settled — STORED (`settled`), or
                    // worked out here but not yet written (`state`). The server grades the
                    // tree on demand before it clears (clearBranchPremise.js), so a premise
                    // that settled by its window lapsing, which no sweep had written, is
                    // written and cleared by the click; if the server still disagrees it
                    // answers 409 and the panel says why. Plus a resolved ROOT, which retires
                    // every conditional hanging off it and persists nothing on the branch.
                    clearable={
                      !!bPremise?.settled ||
                      // Not `state`: a debug premise override forces that, view-only.
                      !!bPremise?.computedSettled ||
                      !!laneVerdict.hasPersistedTerminalVerdict
                    }
                    onClear={() => clearBranchPremise && clearBranchPremise(bIdVal)}
                  />
                </TerminalSlot>
              )}

              {/* Branch resolution */}
              {!isCollapsed &&
                !bTerminal &&
                bRSX !== null &&
                bRSX > -40 &&
                bRSX < vw + 40 &&
                bProb != null &&
                !bDist &&
                (!bAnim || bAnim.keepResult || bAnim.revealCount >= bAnim.totalEvents) &&
                (() => {
                  const bp = bProb || 0;
                  const bISz = RES_SZ + 14;
                  const bResAbove = bp >= 0.5;
                  const bSide = leadingSide(bp);
                  const bResCenterY = bResAbove ? bSY - STREAM_HALF / 2 : bSY + STREAM_HALF / 2;
                  const bResTop = bResCenterY - bISz / 2;
                  const bResCol = redBlueTeam ? (bResAbove ? C.blueTeam : C.redTeam) : laneColor;
                  return (
                    <>
                      {/* openReport(branch) is fed a RAW store run (from selectRunTree),
                    not the legacy branch shape. openReport classifies branch-vs-root
                    by `.modification`/`.question` and reads `.contract`/`.events`/
                    `.serverExplanation`/`.report` — all present on a raw run; a fork
                    branch run always carries `.modification`, so it stays classified
                    as a branch overlay. (Legacy `.probability` is absent on a raw run;
                    openReport falls back to `val.contract`, which carries it.) */}
                      {(() => {
                        // Same null-guard as the primary lane's resBg above.
                        const _bResMoment = resDateB
                          ? resolutionMomentLocal(resDateB, laneRoot?.question)
                          : null;
                        const bResBg =
                          _bResMoment &&
                          !isNaN(_bResMoment.getTime()) &&
                          _bResMoment < effectiveToday
                            ? C.past
                            : C.future;
                        return (
                          <div
                            {...ui("timeline.resolution-marker", branch.runId)}
                            onMouseEnter={(e) => {
                              setHov(brh);
                              setHovPos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setHov(null)}
                            onClick={() => openReport(branch)}
                            style={{
                              position: "absolute",
                              left: bRSX - bISz / 2 - 2,
                              top: bResTop,
                              zIndex: 30,
                              cursor: "pointer",
                              animation: isNewBranch ? "popIn 0.45s ease-out forwards" : "none",
                            }}
                          >
                            <div style={{ padding: 2, borderRadius: "50%", background: bResBg }}>
                              <ResolutionIcon
                                p={bp}
                                sz={bISz}
                                color={bResCol}
                                bg={bResBg}
                                pulseDur={ctx.misc.resPulseDur}
                              />
                            </div>
                          </div>
                        );
                      })()}
                      <div
                        style={{
                          position: "absolute",
                          left: bRSX + bISz / 2 + 6,
                          top: bResCenterY + 2,
                          transform: "translateY(-50%)",
                          fontSize: 31,
                          color: bResCol,
                          whiteSpace: "nowrap",
                          pointerEvents: "none",
                          fontWeight: "bold",
                          zIndex: 30,
                          animation: isNewBranch ? "branchFadeIn 0.45s ease-out forwards" : "none",
                        }}
                      >
                        {fmtPct(bSide.p)}%
                        <span style={{ fontSize: 14, fontWeight: 500, marginLeft: 6 }}>
                          {bSide.label}
                        </span>
                      </div>
                    </>
                  );
                })()}
              {/* Branch hover box — a SIBLING of both branch shapes, not nested in either.

                  It used to live inside the `!bDist` pie block, so a branch that renders a
                  DISTRIBUTION (bDist truthy) skipped the pie and its tooltip together: the
                  hover box simply did not exist for multiway branches, and wiring hover
                  handlers onto the marker could not surface it because there was nothing to
                  render. Hoisted here it serves whichever shape drew.

                  `bp` was a local of the pie's IIFE; it is the same `bProb || 0` here. */}
              {hov === brh &&
                (() => {
                  const bPct = Math.round((bProb || 0) * 100);
                  const mainProb = laneRoot?.contract?.probability || 0;
                  const mainPct = Math.round(mainProb * 100);
                  const diff = bPct - mainPct;
                  const diffLabel = diff > 0 ? `+${diff}pp` : `${diff}pp`;
                  const baseQ =
                    laneRoot?.contract?.question || laneRoot?.contract?.title || "this forecast";
                  const assumption = branch.modification || "modified event conditions";
                  const bTitle = `${baseQ} — assuming ${assumption}`;
                  const bNarrative = `Under this branch scenario, the probability shifts to ${fmtPct(bProb || 0)}% (${diffLabel} from the base ${fmtPct(mainProb)}%). This is due to the assumption: ${assumption}.`;
                  const bCalib = lookupCalibrationBin(
                    calibration,
                    CALIBRATION_MODEL_ID,
                    bProb || 0,
                  );
                  return (
                    <Tooltip
                      ev={{
                        title: bTitle,
                        description: bNarrative,
                        isPast: false,
                        probability: bProb || 0,
                        isResolution: true,
                        // The winning outcome, stated the same way the trunk's
                        // tooltip states it. A branch draws the same distribution
                        // marker as the trunk, so its hover had the same reason to
                        // restate the outcome — it was simply never passed one, and
                        // the readout silently did not render.
                        _winner: bDist ? winnerOf(bDist) : null,
                        date: resDateB,
                        calibration: bCalib,
                        _calibrationEnabled: bCalib?.resolvedTrue != null,
                      }}
                      x={hovPos.x}
                      y={hovPos.y}
                      zoom={uiZoom}
                    />
                  );
                })()}
            </div>
          );
        });
      })()}
    </div>
  );
}
